import React, { useState } from "react";
import { Bookmark, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";

export const DEFAULT_VIEW = "";

function NameDialog({ open, title, initial, onClose, onSubmit }) {
  const [name, setName] = useState(initial);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="book-view-name-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Saved views are shared with every admin.</DialogDescription>
        </DialogHeader>
        <input className="input-flat my-3" autoFocus maxLength={60} value={name} onChange={(e) => setName(e.target.value)}
               placeholder="e.g. Current clients" data-testid="book-view-name-input" />
        <DialogFooter>
          <button className="btn-ghost" onClick={onClose} data-testid="book-view-name-cancel">Cancel</button>
          <button className="btn-primary" disabled={!name.trim()} onClick={() => onSubmit(name.trim())} data-testid="book-view-name-save">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BookViewBar({ views, viewId, onViewChange, showAll, onShowAll, visibleNames, onViewsChanged }) {
  const [dialog, setDialog] = useState(null);
  const current = views.find((v) => v.id === viewId);

  const call = async (request, message) => {
    try { const { data } = await request(); toast.success(message); setDialog(null); return data; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
  };
  const saveNew = async (name) => {
    const body = showAll || current?.all_employers ? { name, all_employers: true }
      : { name, employers: [...new Set([...(current?.employers || []), ...visibleNames])] };
    const view = await call(() => api.post("/admin/book-summary/views", body), "View saved");
    if (view) { await onViewsChanged(); onViewChange(view.id); }
  };
  const rename = async (name) => {
    const body = { name, all_employers: current.all_employers, employers: current.employers };
    if (await call(() => api.put(`/admin/book-summary/views/${current.id}`, body), "View renamed")) onViewsChanged();
  };
  const remove = async () => {
    if (!window.confirm(`Delete the shared view "${current.name}" for every admin?`)) return;
    if (await call(() => api.delete(`/admin/book-summary/views/${current.id}`), "View deleted")) { onViewChange(DEFAULT_VIEW); onViewsChanged(); }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="book-view-bar">
      <label className="flex items-center gap-2 text-xs eyebrow">
        View
        <select className="input-flat !py-1.5 text-sm normal-case tracking-normal" value={viewId} onChange={(e) => onViewChange(e.target.value)}
                disabled={showAll} data-testid="book-view-select">
          <option value={DEFAULT_VIEW}>Default (Settings)</option>
          {views.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </label>
      {current && !showAll && (
        <>
          <button className="btn-ghost !py-1 !px-2 text-xs inline-flex items-center gap-1" onClick={() => setDialog("rename")} data-testid="book-view-rename"><Pencil size={12} /> Rename</button>
          <button className="btn-ghost !py-1 !px-2 text-xs inline-flex items-center gap-1" onClick={remove} data-testid="book-view-delete"><Trash2 size={12} /> Delete</button>
        </>
      )}
      <button className="btn-ghost !py-1 !px-2 text-xs inline-flex items-center gap-1" onClick={() => setDialog("new")} data-testid="book-view-save-as"><Bookmark size={12} /> Save as view</button>
      <label className="ml-auto flex items-center gap-2 text-sm cursor-pointer" data-testid="book-show-all-label">
        <input type="checkbox" checked={showAll} onChange={(e) => onShowAll(e.target.checked)} data-testid="book-show-all-toggle" />
        Show all employers
      </label>
      {dialog && (
        <NameDialog open title={dialog === "new" ? "Save current selection as a view" : "Rename view"}
                    initial={dialog === "rename" ? current.name : ""} onClose={() => setDialog(null)}
                    onSubmit={dialog === "new" ? saveNew : rename} />
      )}
    </div>
  );
}
